import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import { motion } from "motion/react";
import { AlertTriangle, Check, CircleDashed, CircleDot, FilePlus2, FileText, Loader2, Plus, Sparkles, Trash2, UploadCloud, X } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatAedCompact, geographyText, useLocale, useTranslation } from "@ncp/i18n";
import { saveFailureText, useSave } from "@ncp/ui";
import { PHASES, bi, type Bi, type PhaseKey } from "../mission/engine";
import { phaseOfState, phaseTitle } from "../mission/lead";
import { docType } from "../mission/text";
import { tools, type CaseListItem, type EcFormView, type StaffDocument } from "../mission/tools";
import type { MissionOptions } from "../mission/agents/assessment";
import { useT } from "./kit";
import { useFileRefusal } from "../../case/filingUploads";
import { FigureInfo } from "../../case/MethodInfo";

/** States in which a notification is still in intake: the filings the officer would screen next. */
const INTAKE_STATES = ["Received", "Screening", "FormalReview", "AwaitingCompletion", "Draft", "PreNotification"];

/** What the officer may drop: the platform's accepted document types. */
const ACCEPT = { "application/pdf": [".pdf"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "text/plain": [".txt"], "text/csv": [".csv"], "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] };

/** The Article 10 slot a dropped file most likely belongs to, read from its name; the readers confirm it from the text. */
function slotOf(name: string): { category: string; key: string } {
  const n = name.toLowerCase();
  if (/(agreement|\bspa\b|sale|purchase|merger)/.test(n)) return { category: "TransactionAgreement", key: "art10-agreement" };
  if (/(financial|statement|accounts|audited|balance)/.test(n)) return { category: "FinancialStatements", key: "art10-financials" };
  if (/(market)/.test(n)) return { category: "MarketReport", key: "art10-market-report" };
  if (/(ownership|structure|org|chart|shareholder)/.test(n)) return { category: "OrgChart", key: "art10-orgchart" };
  if (/(attorney|\bpoa\b|mandate|authori[sz]ation)/.test(n)) return { category: "PowerOfAttorney", key: "art10-poa" };
  if (/(overlap)/.test(n)) return { category: "OverlapAnalysis", key: "art10-overlaps" };
  if (/(summary|public|non-confidential)/.test(n)) return { category: "PublicSummary", key: "art10-public-summary" };
  return { category: "Other", key: "" };
}

/** How many agents each wave dispatches for this file (the document readers depend on the files). */
export function rosterOf(documents: number, begin: PhaseKey = "screening"): Record<PhaseKey, number> {
  const roster = { screening: documents + 8, publication: 5, review: 11, assessment: 4, decision: 4 };
  // A review that begins after screening reads the Department's memory in its first wave.
  if (begin !== "screening") roster[begin] += 1;
  return roster;
}

interface Draft {
  title: string; transactionType: string; considerationAed: string; signingDate: string; expectedClosingDate: string; description: string;
  acquirer: { name: string; licence: string; emirate: string }; target: { name: string; licence: string; emirate: string };
  stake: string; preStake: string; turnover: string; fiscalYear: string;
  product: string; geography: string; marketSize: string; acquirerShare: string; targetShare: string; competitors: Array<{ name: string; share: string }>;
}
const EMPTY: Draft = {
  title: "", transactionType: "Acquisition", considerationAed: "", signingDate: "", expectedClosingDate: "", description: "",
  acquirer: { name: "", licence: "", emirate: "Dubai" }, target: { name: "", licence: "", emirate: "Abu Dhabi" },
  stake: "", preStake: "0", turnover: "", fiscalYear: String(new Date().getFullYear() - 1),
  product: "", geography: "United Arab Emirates", marketSize: "", acquirerShare: "", targetShare: "", competitors: [{ name: "", share: "" }],
};

/**
 * Intake: the notification and its files, before the agents are sent in. A received filing shows as filed (the applicant's form cannot be
 * edited once submitted) and may take more documents; a new notification is typed here, created as a draft and analysed the same way.
 */
export function IntakeView({ caseId, onAnalyse, onOpenCase, onNew }: {
  caseId?: string; onAnalyse: (caseId: string, options: MissionOptions, startAt: PhaseKey) => void; onOpenCase: (id: string) => void; onNew: () => void;
}) {
  const api = useApi();
  const { t } = useTranslation();
  const tx = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const cases = useQuery({ queryKey: ["agentic", "cases"], queryFn: () => api.get<{ items: CaseListItem[] }>("/api/cases", { type: "EconomicConcentration", pageSize: 100 }) });
  const form = useQuery({ queryKey: ["agentic", "form", caseId], enabled: !!caseId, queryFn: () => tools.form(api, caseId!) });
  const documents = useQuery({ queryKey: ["agentic", "documents", caseId], enabled: !!caseId, queryFn: () => tools.documents(api, caseId!), refetchInterval: (q) => (q.state.data?.some((d) => d.extractionStatus === "Pending" || d.extractionStatus === "Processing") ? 2500 : false) });
  const readiness = useQuery({ queryKey: ["agentic", "readiness", caseId], enabled: !!caseId, queryFn: () => tools.readiness(api, caseId!) });
  const ai = useQuery({ queryKey: ["agentic", "ai-status"], queryFn: () => tools.aiStatus(api), staleTime: 60_000 });
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [queued, setQueued] = useState<File[]>([]);
  // "Translation of" (S 6.13): for a queued file, the queued file it translates; for a document on file, the document it translates.
  const [translationOf, setTranslationOf] = useState<Record<string, string>>({});
  const refusal = useFileRefusal();
  const [live, setLive] = useState(true);
  const [startAt, setStartAt] = useState<PhaseKey | null>(null);
  const isNew = !caseId;
  const begin = startAt ?? (form.data ? phaseOfState(form.data.state) : "screening");

  // Both report a failure on the screen itself, with its cause.
  const upload = useSave({
    report: "silent",
    mutationFn: async ({ id, files, pairs = {} }: { id: string; files: File[]; pairs?: Record<string, string> }) => {
      // Originals go first, so a translation is filed against the document it translates (S 6.13).
      const ordered = [...files].sort((a, b) => Number(!!pairs[fileKey(a)]) - Number(!!pairs[fileKey(b)]));
      const filed = new Map<string, string>();
      for (const file of ordered) {
        const slot = slotOf(file.name);
        const body = new FormData();
        body.append("file", file);
        body.append("category", slot.category);
        if (slot.key) body.append("checklistItemKey", slot.key);
        const original = pairs[fileKey(file)] ? filed.get(pairs[fileKey(file)]!) : undefined;
        if (original) body.append("translationOf", original);
        const doc = await api.upload<{ id: string }>(`/api/cases/${id}/documents`, body);
        filed.set(fileKey(file), doc.id);
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["agentic", "documents", caseId] }); void qc.invalidateQueries({ queryKey: ["agentic", "readiness", caseId] }); },
  });

  const create = useSave({
    report: "silent",
    // The answer is the new file's id, read from the API's confirmed answers above it.
    expect: "any",
    mutationFn: async () => {
      const created = await api.post<{ caseId: string; caseNumber: string }>("/api/ec", { title: draft.title.trim(), titleAr: null });
      await api.put(`/api/ec/${created.caseId}`, inputOf(draft));
      if (queued.length) await upload.mutateAsync({ id: created.caseId, files: queued, pairs: translationOf });
      return created.caseId;
    },
  });

  // A pairing an officer makes on a document already on file goes through the officer's link, which compares the two at once.
  const link = useSave({
    mutationFn: ({ id, documentId, originalId }: { id: string; documentId: string; originalId: string }) => api.put<StaffDocument>(`/api/cases/${id}/documents/${documentId}/translation`, { originalId: originalId || null }),
    expect: "record",
    saved: (_, v) => t(v.originalId ? "backoffice:filingUploads.linked" : "backoffice:filingUploads.unlinked"),
    onSuccess: (_, v) => { setTranslationOf((p) => ({ ...p, [v.documentId]: v.originalId })); void qc.invalidateQueries({ queryKey: ["agentic", "documents", caseId] }); },
  });

  const drop = useDropzone({
    accept: ACCEPT,
    // A legacy .doc or any type the platform does not read is refused with the reason, never dropped silently (S 6.12).
    onDropRejected: refusal.onRejected,
    onDrop: (files) => {
      if (!files.length) return;
      if (caseId) upload.mutate({ id: caseId, files });
      else setQueued((q) => [...q, ...files]);
    },
  });

  const docCount = isNew ? queued.length : documents.data?.length ?? 0;
  const roster = rosterOf(docCount, begin);
  const total = PHASES.slice(PHASES.indexOf(begin)).reduce((s, p) => s + roster[p], 0);
  const options: MissionOptions = { live: live && !!ai.data && !ai.data.offline, aiOnline: !!ai.data && !ai.data.offline };
  const ready = isNew ? draft.title.trim().length > 3 && !!draft.acquirer.name && !!draft.target.name && !!draft.product : !!form.data;
  const busy = create.isPending || upload.isPending;

  const analyse = async () => {
    if (caseId) { onAnalyse(caseId, options, begin); return; }
    const id = await create.mutateAsync();
    onAnalyse(id, options, "screening");
  };

  const queue = useMemo(() => (cases.data?.items ?? []).filter((c) => INTAKE_STATES.includes(c.state) && !/^(Economic concentration notification|إخطار بتركز اقتصادي|Probe|Scan test|Smoke)/.test(c.title)).slice(0, 10), [cases.data]);
  const others = useMemo(() => (cases.data?.items ?? []).filter((c) => !INTAKE_STATES.includes(c.state)), [cases.data]);

  return (
    <div className="relative z-10 min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1520px] px-4 pb-10 pt-6 lg:px-8">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--agx-gold)]">{t("backoffice:agentic.intake.kicker")}</p>
            <h1 className="mt-1.5 font-heading text-[28px] font-semibold leading-tight sm:text-[34px]">
              {t("backoffice:agentic.intake.headline1")} <span className="agx-text-grad">{t("backoffice:agentic.intake.headline2")}</span>
            </h1>
            <p className="mt-2 text-[14px] leading-6 agx-dim">{t("backoffice:agentic.intake.lede")}</p>
          </div>
        </div>

        <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
          <button type="button" onClick={onNew} className={`agx-btn h-auto shrink-0 flex-col items-start px-3 py-2 ${isNew ? "!border-[color:var(--agx-gold)] !bg-[color:var(--agx-gold)]/10" : ""}`}>
            <span className="flex items-center gap-1.5 text-[12.5px]"><FilePlus2 className="size-3.5 text-[color:var(--agx-gold)]" />{t("backoffice:agentic.intake.new")}</span>
            <span className="text-[11px] font-normal agx-dim">{t("backoffice:agentic.intake.newHint")}</span>
          </button>
          {queue.map((c) => (
            <button key={c.id} type="button" onClick={() => onOpenCase(c.id)} className={`agx-btn h-auto w-64 shrink-0 flex-col items-start px-3 py-2 ${c.id === caseId ? "!border-[color:var(--agx-cyan)] !bg-[color:var(--agx-cyan)]/10" : ""}`}>
              <span className="agx-mono text-[11px] text-[color:var(--agx-cyan)]" dir="ltr">{c.caseNumber}</span>
              <span className="w-full truncate text-start text-[12.5px] [unicode-bidi:plaintext]" title={locale === "ar" && c.titleAr ? c.titleAr : c.title}>{locale === "ar" && c.titleAr ? c.titleAr : c.title}</span>
              <span className="w-full truncate text-start text-[11px] font-normal agx-dim">{locale === "ar" ? c.stateLabelAr : c.stateLabel}</span>
            </button>
          ))}
          {others.length ? (
            <select className="agx-input h-auto w-56 shrink-0 self-stretch" value="" onChange={(e) => e.target.value && onOpenCase(e.target.value)} aria-label={t("backoffice:agentic.intake.other")}>
              <option value="">{t("backoffice:agentic.intake.other")}</option>
              {others.map((c) => <option key={c.id} value={c.id}>{c.caseNumber} · {locale === "ar" && c.titleAr ? c.titleAr : c.title}</option>)}
            </select>
          ) : null}
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)]">
          {isNew ? <DraftForm draft={draft} setDraft={setDraft} /> : form.data ? <FiledForm form={form.data} /> : <PanelSkeleton />}

          <div className="space-y-4">
            <section className="agx-glass rounded-md p-4">
              <div className="mb-3 flex items-center gap-2">
                <UploadCloud className="size-4 text-[color:var(--agx-cyan)]" />
                <h2 className="text-[14px] font-semibold">{t(isNew ? "backoffice:agentic.intake.files" : "backoffice:agentic.intake.filedPack")}</h2>
                <span className="agx-mono ms-auto text-[11px] agx-dim">{docCount}</span>
              </div>
              {/* A filed notification holds the company's own documents; a late document can still be added, without a drop area in the way. */}
              <div {...drop.getRootProps()} className={`agx-drop flex cursor-pointer items-center text-center ${isNew ? "flex-col gap-1.5 px-4 py-6" : "gap-2 px-3 py-2 text-start"}`} data-active={drop.isDragActive}>
                <input {...drop.getInputProps()} />
                <UploadCloud className={`${isNew ? "size-7" : "size-4"} shrink-0 text-[color:var(--agx-cyan)]`} />
                <p className={isNew ? "text-[13px] font-medium" : "text-[12px] agx-dim"}>{t(drop.isDragActive ? "backoffice:agentic.intake.dropActive" : isNew ? "backoffice:agentic.intake.drop" : "backoffice:agentic.intake.dropLate")}</p>
                {isNew ? <p className="text-[11.5px] agx-dim">{t("backoffice:agentic.intake.dropHint")}</p> : null}
                {upload.isPending ? <p className="flex items-center gap-1.5 text-[11.5px] text-[color:var(--agx-gold)]"><Loader2 className="size-3 agx-spin" />{t("backoffice:agentic.intake.uploading")}</p> : null}
                {upload.isError ? <p className="text-[11.5px] text-[color:var(--agx-rose)]">{saveFailureText(upload.error)}</p> : null}
              </div>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {isNew
                  ? queued.map((f, i) => <FileTile key={`${f.name}-${i}`} name={f.name} size={f.size} type={docType(slotOf(f.name).category)} status="queued" onRemove={() => setQueued((q) => q.filter((_, j) => j !== i))}
                      translation={<TranslationOf options={queued.filter((o) => fileKey(o) !== fileKey(f)).map((o) => ({ id: fileKey(o), fileName: o.name }))} value={translationOf[fileKey(f)] ?? ""} onChange={(v) => setTranslationOf((p) => ({ ...p, [fileKey(f)]: v }))} />} />)
                  : documents.data?.map((d) => <FileTile key={d.id} name={d.fileName} size={d.sizeBytes} pages={d.pageCount} type={d.checklistTitle ? bi(d.checklistTitle, d.checklistTitleAr ?? d.checklistTitle) : docType(d.category)} status={statusOf(d)}
                      translation={<TranslationOf options={(documents.data ?? []).filter((o) => o.id !== d.id).map((o) => ({ id: o.id, fileName: o.fileName }))} value={translationOf[d.id] ?? d.translationOfId ?? ""} disabled={link.isPending} onChange={(v) => caseId && link.mutate({ id: caseId, documentId: d.id, originalId: v })} />} />)}
              </ul>
              {!isNew && readiness.data ? (
                <div className="mt-4 border-t border-[color:var(--agx-line)] pt-3">
                  <p className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] agx-faint">{t("backoffice:agentic.intake.article10")}<span className="agx-mono ms-auto inline-flex items-center gap-0.5 normal-case tracking-normal">{readiness.data.score}/100<FigureInfo measure="filingReadiness" className="size-4" /></span></p>
                  <ul className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                    {readiness.data.items.map((i) => (
                      <li key={i.key} className="flex min-w-0 items-center gap-1.5 text-[11.5px]">
                        {i.status === "Complete" ? <Check className="size-3 shrink-0 text-[color:var(--agx-green)]" /> : i.status === "Partial" ? <CircleDot className="size-3 shrink-0 text-[color:var(--agx-amber)]" /> : i.mandatory ? <X className="size-3 shrink-0 text-[color:var(--agx-rose)]" /> : <CircleDashed className="size-3 shrink-0 agx-faint" />}
                        <span className="truncate agx-dim">{tx(bi(i.titleEn, i.titleAr))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>

            <section className="agx-glass relative overflow-hidden rounded-md p-4">
              <div className="pointer-events-none absolute -end-24 -top-24 size-72 rounded-full bg-[radial-gradient(circle,var(--agx-glow-1),transparent_65%)]" />
              <h2 className="flex items-center gap-2 text-[14px] font-semibold"><Sparkles className="size-4 text-[color:var(--agx-gold)]" />{t("backoffice:agentic.intake.team")}</h2>
              <p className="mt-1 text-[12px] agx-dim">{t("backoffice:agentic.intake.teamHint", { count: total })}</p>
              <div className="mt-3 space-y-2">
                {PHASES.map((p, i) => {
                  const on = PHASES.indexOf(p) >= PHASES.indexOf(begin);
                  return (
                    <div key={p} className={`flex items-center gap-3 ${on ? "" : "opacity-35"}`}>
                      <span className="w-40 shrink-0 truncate text-[11.5px] agx-dim"><span className="agx-mono text-[color:var(--agx-gold)]">{i + 1}</span> · {tx(phaseTitle(p))}</span>
                      <div className="flex flex-wrap gap-1">
                        {Array.from({ length: roster[p] }, (_, j) => (
                          <motion.span key={j} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.02 * (i * 10 + j) }} className="size-2.5 rounded-full" data-squad={ROSTER_SQUADS[p][j % ROSTER_SQUADS[p].length]} style={{ background: "var(--sq)", opacity: on ? 1 : 0.35 }} />
                        ))}
                      </div>
                      <span className="agx-mono ms-auto text-[11px] agx-faint">{roster[p]}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="agx-label">{t("backoffice:agentic.intake.begin")}</span>
                  <select className="agx-input" value={begin} disabled={isNew} onChange={(e) => setStartAt(e.target.value as PhaseKey)}>
                    {PHASES.map((p) => <option key={p} value={p}>{tx(phaseTitle(p))}</option>)}
                  </select>
                </label>
                <label className={`flex items-start gap-2 rounded-[5px] border border-[color:var(--agx-line)] p-2.5 ${ai.data && !ai.data.offline ? "" : "opacity-50"}`}>
                  <input type="checkbox" className="mt-0.5 accent-[#e9c46a]" checked={live && !!ai.data && !ai.data.offline} disabled={!ai.data || ai.data.offline} onChange={(e) => setLive(e.target.checked)} />
                  <span className="text-[11.5px] leading-[1.45]">
                    <span className="font-semibold">{t("backoffice:agentic.intake.live")}</span>
                    <span className="block agx-dim">{t(ai.data && !ai.data.offline ? "backoffice:agentic.intake.liveHint" : "backoffice:agentic.intake.liveOffline")}</span>
                  </span>
                </label>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 z-20 border-t border-[color:var(--agx-line)] bg-[color:var(--agx-bg)]/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-[1520px] flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-1.5 rtl:space-x-reverse">
              {(["documents", "legal", "market", "intel", "drafting", "quality"] as const).map((sq) => <span key={sq} data-squad={sq} className="size-5 rounded-full border-2 border-[color:var(--agx-bg)]" style={{ background: "var(--sq)" }} />)}
            </div>
            <p className="text-[12.5px] agx-dim">{t("backoffice:agentic.intake.teamHint", { count: total })}</p>
          </div>
          {create.isError ? <p className="text-[12px] text-[color:var(--agx-rose)]">{saveFailureText(create.error)}</p> : null}
          {!ready && isNew ? <p className="text-[11.5px] agx-dim">{t("backoffice:agentic.intake.needs")}</p> : null}
          <button type="button" className="agx-cta ms-auto" disabled={!ready || busy} onClick={() => void analyse()}>
            {busy ? <Loader2 className="size-5 agx-spin" /> : <Sparkles className="size-5" />}
            {t("backoffice:agentic.intake.analyse")}
            <span className="agx-mono rounded-md bg-black/15 px-1.5 py-0.5 text-[12px]">{total}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const ROSTER_SQUADS: Record<PhaseKey, string[]> = {
  screening: ["memory", "documents", "documents", "legal", "intel", "quality", "legal", "documents", "intel"],
  publication: ["drafting", "quality", "intel", "legal", "legal"],
  review: ["market", "market", "intel", "market", "market", "market", "intel", "legal", "intel", "intel", "quality"],
  assessment: ["drafting", "quality", "market", "market"],
  decision: ["drafting", "drafting", "drafting", "quality"],
};

function statusOf(d: StaffDocument): "ready" | "processing" | "failed" {
  return d.extractionStatus === "Completed" ? "ready" : d.extractionStatus === "Failed" ? "failed" : "processing";
}

/** A dropped file's identity before it has an id on the case. */
const fileKey = (f: File) => `${f.name}:${f.size}`;

/** "Translation of" on a file tile (S 6.13), styled for the mission view; shown only when there is another file to choose. */
function TranslationOf({ options, value, onChange, disabled }: { options: Array<{ id: string; fileName: string }>; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  if (options.length === 0) return null;
  return (
    <select className="agx-input mt-1 h-7 w-full py-0 text-[11px]" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} aria-label={t("backoffice:filingUploads.translationOf")} title={t("backoffice:filingUploads.translationHint")}>
      <option value="">{t("backoffice:filingUploads.translationOf")}: {t("backoffice:filingUploads.notATranslation")}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{t("backoffice:filingUploads.translationOf")}: {o.fileName}</option>)}
    </select>
  );
}

function FileTile({ name, size, pages, type, status, onRemove, translation }: { name: string; size: number; pages?: number | null; type: Bi; status: "ready" | "processing" | "failed" | "queued"; onRemove?: () => void; translation?: React.ReactNode }) {
  const t = useT();
  const { t: tr } = useTranslation();
  return (
    <motion.li layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex min-w-0 items-center gap-2.5 rounded-[5px] border border-[color:var(--agx-line)] agx-wash p-2">
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-[color:var(--agx-cyan)]/10"><FileText className="size-4 text-[color:var(--agx-cyan)]" /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-medium" dir="ltr">{name}</span>
        <span className="block truncate text-[11px] agx-dim">{t(type)} · {tr("backoffice:workspace.docSize", { kb: Math.max(1, Math.round(size / 1024)) })}{pages ? ` · ${tr("backoffice:workspace.docPages", { count: pages })}` : ""}</span>
        {translation}
      </span>
      {status === "processing" ? <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-cyan)" }}><Loader2 className="size-2.5 agx-spin" />{tr("backoffice:agentic.intake.reading")}</span>
        : status === "failed" ? <AlertTriangle className="size-3.5 text-[color:var(--agx-rose)]" />
          : status === "queued" ? <button type="button" onClick={onRemove} className="agx-btn size-7 p-0" data-tone="ghost" aria-label={tr("backoffice:agentic.intake.remove")}><Trash2 className="size-3.5" /></button>
            : <Check className="size-3.5 text-[color:var(--agx-green)]" />}
    </motion.li>
  );
}

function PanelSkeleton() {
  return <div className="agx-glass h-[520px] animate-pulse rounded-md" />;
}

// ---------------------------------------------------------------- the form

function Field({ label, value, missing, wide, mono }: { label: string; value?: React.ReactNode; missing?: boolean; wide?: boolean; mono?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <span className="agx-label">{label}</span>
      <div className={`min-h-[34px] rounded-[5px] border px-2.5 py-1.5 text-[13px] ${missing ? "border-[color:var(--agx-amber)]/50 bg-[color:var(--agx-amber)]/[0.06] text-[color:var(--agx-amber)]" : "border-[color:var(--agx-line)] agx-wash"} ${mono ? "agx-mono" : ""}`}>
        {missing ? t("backoffice:agentic.intake.notOnForm") : value}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-gold)]"><span className="h-px w-4 bg-[color:var(--agx-gold)]/60" />{title}</h3>
      {children}
    </section>
  );
}

/** A received notification as the applicant filed it (form MOE/CD-F-01). Blank fields are marked: the agents will look for them in the documents. */
function FiledForm({ form }: { form: EcFormView }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const parties = form.parties;
  return (
    <section className="agx-glass space-y-5 rounded-md p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-semibold">{t("backoffice:agentic.intake.form")}</h2>
        <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-cyan)" }}>MOE/CD-F-01</span>
        <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-violet)" }}>{t("backoffice:agentic.intake.asFiled")}</span>
        <span className="agx-mono ms-auto text-[11px] agx-dim" dir="ltr">{form.caseNumber}</span>
      </div>
      <Section title={t("backoffice:agentic.intake.transaction")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field wide label={t("backoffice:agentic.intake.title")} value={ar && form.titleAr ? form.titleAr : form.title} />
          <Field label={t("backoffice:agentic.intake.type")} value={t(`backoffice:agentic.types.${form.transactionType}`, { defaultValue: form.transactionType })} />
          <Field label={t("backoffice:agentic.intake.consideration")} value={form.considerationAed != null ? formatAedCompact(form.considerationAed, locale) : undefined} missing={form.considerationAed == null} />
          <Field label={t("backoffice:agentic.intake.signing")} value={form.signingDate} mono missing={!form.signingDate} />
          <Field label={t("backoffice:agentic.intake.closing")} value={form.expectedClosingDate} mono missing={!form.expectedClosingDate} />
          <Field wide label={t("backoffice:agentic.intake.description")} value={ar && form.transactionDescriptionAr ? form.transactionDescriptionAr : form.transactionDescription} />
        </div>
      </Section>
      <Section title={t("backoffice:agentic.intake.parties")}>
        <ul className="space-y-2">
          {parties.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[5px] border border-[color:var(--agx-line)] agx-wash px-3 py-2">
              <span className="agx-chip" style={{ ["--c" as string]: p.role === "AcquiringParty" ? "var(--agx-gold)" : p.role === "TargetParty" ? "var(--agx-cyan)" : "var(--agx-violet)" }}>{t(`backoffice:agentic.roles.${p.role}`, { defaultValue: p.role })}</span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{ar && p.nameAr ? p.nameAr : p.name}</span>
              {p.registryNumber ? <span className="agx-mono text-[11px] agx-dim" dir="ltr">{p.registryNumber}</span> : null}
              {p.emirate ? <span className="text-[11px] agx-faint">{geographyText(p.emirate, locale)}</span> : null}
            </li>
          ))}
        </ul>
      </Section>
      <Section title={t("backoffice:agentic.intake.control")}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t("backoffice:agentic.intake.stake")} value={form.acquiredStakePercent != null ? `${form.acquiredStakePercent}%` : undefined} missing={form.acquiredStakePercent == null} mono />
          <Field label={t("backoffice:agentic.intake.preStake")} value={form.preTransactionStakePercent != null ? `${form.preTransactionStakePercent}%` : "0%"} mono />
          <Field label={t("backoffice:agentic.intake.controlType")} value={t(`backoffice:agentic.control.${form.controlType}`, { defaultValue: form.controlType })} missing={form.controlType === "Unknown"} />
        </div>
      </Section>
      <Section title={t("backoffice:agentic.intake.markets")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("backoffice:agentic.intake.turnover")} value={formatAedCompact(form.combinedUaeTurnoverAed, locale)} />
          <Field label={t("backoffice:agentic.intake.fiscalYear")} value={form.fiscalYear} mono />
        </div>
        <div className="mt-3 space-y-2">
          {form.markets.map((m) => (
            <div key={m.id} className="rounded-[5px] border border-[color:var(--agx-line)] agx-wash p-3">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p className="text-[13px] font-medium">{ar && m.productMarketAr ? m.productMarketAr : m.productMarket}</p>
                <p className="text-[11.5px] agx-dim">{geographyText(m.geographicMarket, locale)} · {formatAedCompact(m.marketSizeAed, locale)} · {m.isicCode ? t("common:isic", { code: m.isicCode }) : "—"}</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(m.partyShares).map(([n, s]) => <span key={n} className="agx-chip normal-case tracking-normal" style={{ ["--c" as string]: "var(--agx-gold)" }}>{n === "Others" ? t("backoffice:dataBank.others") : n} · {s}%</span>)}
                {Object.entries(m.competitorShares).map(([n, s]) => <span key={n} className="agx-chip normal-case tracking-normal" style={{ ["--c" as string]: "var(--agx-dim)" }}>{n} · {s}%</span>)}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </section>
  );
}

/** A new notification typed by the officer (a filing received outside the portal). */
function DraftForm({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { t } = useTranslation();
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });
  const input = (label: string, value: string, onChange: (v: string) => void, opts: { type?: string; placeholder?: string; wide?: boolean; dir?: "ltr" } = {}) => (
    <label className={`block ${opts.wide ? "sm:col-span-2" : ""}`}>
      <span className="agx-label">{label}</span>
      <input className="agx-input" type={opts.type ?? "text"} value={value} placeholder={opts.placeholder} dir={opts.dir} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
  return (
    <section className="agx-glass space-y-5 rounded-md p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold">{t("backoffice:agentic.intake.newForm")}</h2>
        <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-cyan)" }}>MOE/CD-F-01</span>
      </div>
      <Section title={t("backoffice:agentic.intake.transaction")}>
        <div className="grid gap-3 sm:grid-cols-2">
          {input(t("backoffice:agentic.intake.title"), draft.title, (v) => set("title", v), { wide: true })}
          <label className="block">
            <span className="agx-label">{t("backoffice:agentic.intake.type")}</span>
            <select className="agx-input" value={draft.transactionType} onChange={(e) => set("transactionType", e.target.value)}>
              {["Acquisition", "Merger", "JointVenture", "AssetTransfer", "ControlChange"].map((k) => <option key={k} value={k}>{t(`backoffice:agentic.types.${k}`)}</option>)}
            </select>
          </label>
          {input(t("backoffice:agentic.intake.considerationAed"), draft.considerationAed, (v) => set("considerationAed", v), { type: "number", dir: "ltr" })}
          {input(t("backoffice:agentic.intake.signing"), draft.signingDate, (v) => set("signingDate", v), { type: "date" })}
          {input(t("backoffice:agentic.intake.closing"), draft.expectedClosingDate, (v) => set("expectedClosingDate", v), { type: "date" })}
          {input(t("backoffice:agentic.intake.description"), draft.description, (v) => set("description", v), { wide: true })}
        </div>
      </Section>
      <Section title={t("backoffice:agentic.intake.parties")}>
        {(["acquirer", "target"] as const).map((role) => (
          <div key={role} className="mb-2 grid gap-2 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.8fr)]">
            {input(t(`backoffice:agentic.intake.${role}`), draft[role].name, (v) => set(role, { ...draft[role], name: v }))}
            {input(t("backoffice:agentic.intake.licence"), draft[role].licence, (v) => set(role, { ...draft[role], licence: v }), { placeholder: "CN-0000000", dir: "ltr" })}
            {input(t("backoffice:agentic.intake.emirate"), draft[role].emirate, (v) => set(role, { ...draft[role], emirate: v }))}
          </div>
        ))}
      </Section>
      <Section title={t("backoffice:agentic.intake.control")}>
        <div className="grid gap-3 sm:grid-cols-2">
          {input(t("backoffice:agentic.intake.stake"), draft.stake, (v) => set("stake", v), { type: "number", dir: "ltr" })}
          {input(t("backoffice:agentic.intake.preStake"), draft.preStake, (v) => set("preStake", v), { type: "number", dir: "ltr" })}
        </div>
      </Section>
      <Section title={t("backoffice:agentic.intake.markets")}>
        <div className="grid gap-3 sm:grid-cols-2">
          {input(t("backoffice:agentic.intake.turnoverAed"), draft.turnover, (v) => set("turnover", v), { type: "number", dir: "ltr" })}
          {input(t("backoffice:agentic.intake.fiscalYear"), draft.fiscalYear, (v) => set("fiscalYear", v), { type: "number", dir: "ltr" })}
          {input(t("backoffice:agentic.intake.product"), draft.product, (v) => set("product", v))}
          {input(t("backoffice:agentic.intake.geography"), draft.geography, (v) => set("geography", v))}
          {input(t("backoffice:agentic.intake.marketSizeAed"), draft.marketSize, (v) => set("marketSize", v), { type: "number", dir: "ltr" })}
          <div className="grid grid-cols-2 gap-2">
            {input(t("backoffice:agentic.intake.acquirerShare"), draft.acquirerShare, (v) => set("acquirerShare", v), { type: "number", dir: "ltr" })}
            {input(t("backoffice:agentic.intake.targetShare"), draft.targetShare, (v) => set("targetShare", v), { type: "number", dir: "ltr" })}
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <span className="agx-label">{t("backoffice:agentic.intake.competitors")}</span>
          {draft.competitors.map((c, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_110px_auto] gap-2">
              <input className="agx-input" value={c.name} placeholder={t("backoffice:agentic.intake.competitor")} onChange={(e) => set("competitors", draft.competitors.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <input className="agx-input" type="number" dir="ltr" value={c.share} placeholder="%" onChange={(e) => set("competitors", draft.competitors.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} />
              <button type="button" className="agx-btn size-9 p-0" data-tone="ghost" onClick={() => set("competitors", draft.competitors.filter((_, j) => j !== i))} aria-label={t("backoffice:agentic.intake.remove")}><Trash2 className="size-3.5" /></button>
            </div>
          ))}
          <button type="button" className="agx-btn h-8 px-2.5 text-[12px]" onClick={() => set("competitors", [...draft.competitors, { name: "", share: "" }])}><Plus className="size-3.5" />{t("backoffice:agentic.intake.addCompetitor")}</button>
        </div>
      </Section>
    </section>
  );
}

const n = (v: string): number | null => (v.trim() === "" || Number.isNaN(Number(v)) ? null : Number(v));

/** The draft as the notification service saves it (EcNotificationInput). */
function inputOf(d: Draft) {
  const shares = (pairs: Array<[string, string]>) => Object.fromEntries(pairs.filter(([name, s]) => name.trim() && n(s) != null).map(([name, s]) => [name.trim(), n(s)!]));
  const stake = n(d.stake);
  const pre = n(d.preStake) ?? 0;
  return {
    title: d.title.trim(), titleAr: null, transactionType: d.transactionType, transactionDescription: d.description.trim(), transactionDescriptionAr: null,
    considerationAed: n(d.considerationAed), signingDate: d.signingDate || null, expectedClosingDate: d.expectedClosingDate || null, controlStructure: null,
    combinedUaeTurnoverAed: n(d.turnover) ?? 0, fiscalYear: n(d.fiscalYear) ?? new Date().getFullYear() - 1, rationale: null, efficienciesClaimed: null,
    confidentialityRequested: false, confidentialityScope: null, otherJurisdictionsNotified: null,
    parties: [
      { id: null, role: "AcquiringParty", name: d.acquirer.name.trim(), nameAr: null, registryNumber: d.acquirer.licence.trim() || null, jurisdiction: "AE", emirate: d.acquirer.emirate || null, contactName: null, contactEmail: null, isSubmitter: true },
      { id: null, role: "TargetParty", name: d.target.name.trim(), nameAr: null, registryNumber: d.target.licence.trim() || null, jurisdiction: "AE", emirate: d.target.emirate || null, contactName: null, contactEmail: null, isSubmitter: false },
    ],
    markets: d.product.trim() ? [{
      id: null, productMarket: d.product.trim(), productMarketAr: null, geographicMarket: d.geography.trim() || "United Arab Emirates", isicCode: null, marketSizeAed: n(d.marketSize) ?? 0,
      partyShares: shares([[d.acquirer.name, d.acquirerShare], [d.target.name, d.targetShare]]), competitorShares: shares(d.competitors.map((c) => [c.name, c.share])), overlapType: "Horizontal",
    }] : [],
    acquiredStakePercent: stake, preTransactionStakePercent: pre, largestOtherShareholderPercent: null,
    controlType: stake == null ? null : pre + stake > 50 ? "Sole" : null, controlRights: stake != null && pre + stake > 50 ? ["BoardMajority"] : [], intraGroup: false,
  };
}
